import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Store, Globe, Copy, Share2, ExternalLink, Trash2, CheckCircle2, Loader2, Crown, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDate, imgUrl } from "@/lib/api";
import { PageHeader, StatusBadge, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import StoreBuilder from "@/pages/agent/StoreBuilder";

export default function AgentStore() {
  const qc = useQueryClient();
  const { data, refetch } = useQuery({ queryKey: ["my-store"], queryFn: async () => (await api.get("/agent/store")).data });
  const { data: storeProducts, refetch: refetchProducts } = useQuery({
    queryKey: ["my-store-products"],
    queryFn: async () => (await api.get("/agent/store/products")).data,
    enabled: !!data?.store,
  });
  const { data: planData } = useQuery({
    queryKey: ["my-plan"],
    queryFn: async () => (await api.get("/agent/plan")).data,
  });
  const [domain, setDomain] = useState("");
  const [justLive, setJustLive] = useState(false);
  const [connectInfo, setConnectInfo] = useState(null);
  const [connectRecords, setConnectRecords] = useState({});

  const setup = useMutation({
    mutationFn: async () => (await api.post("/agent/store/setup", {})).data,
    onSuccess: () => { toast.success("Your store is live!"); setJustLive(true); refetch(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const removeProduct = useMutation({
    mutationFn: async (pid) => api.delete(`/agent/store/products/${pid}`),
    onSuccess: () => { toast.success("Removed from store"); refetchProducts(); refetch(); },
  });
  const syncStore = useMutation({
    mutationFn: async () => (await api.post("/agent/basket/sync-store", {})).data,
    onSuccess: (d) => { toast.success(d.synced ? `${d.synced} product(s) synced into your store` : "Store already in sync with your basket"); refetchProducts(); refetch(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const { data: domainsData, refetch: refetchDomains } = useQuery({
    queryKey: ["agent-domains"],
    queryFn: async () => (await api.get("/agent/domains")).data,
    enabled: !!data?.store,
  });
  const connectDomain = useMutation({
    mutationFn: async () => (await api.post("/agent/domains", { domain })).data,
    onSuccess: (d) => { setConnectInfo(d); setDomain(""); toast.success("Domain added — verify ownership with the TXT record"); refetchDomains(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const verifyDomain = useMutation({
    mutationFn: async (id) => (await api.post(`/agent/domains/${id}/verify`, {})).data,
    onSuccess: (d, id) => {
      if (d.ok) { toast.success("Domain verified — now add the connection record"); setConnectRecords((m) => ({ ...m, [id]: d.connection_records || [] })); }
      else toast.info(d.message || "TXT record not found yet");
      refetchDomains();
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const activateDomain = useMutation({
    mutationFn: async (id) => (await api.post(`/agent/domains/${id}/activate`, {})).data,
    onSuccess: (d) => { if (d.ok) { toast.success("Domain is live with SSL"); } else { toast.info(d.message || "DNS not pointing to DropPartner yet"); } refetchDomains(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const primaryDomain = useMutation({
    mutationFn: async (id) => api.post(`/agent/domains/${id}/primary`, {}),
    onSuccess: () => { toast.success("Primary domain updated"); refetchDomains(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const disconnectDomain = useMutation({
    mutationFn: async (id) => api.delete(`/agent/domains/${id}`),
    onSuccess: () => { toast.success("Domain disconnected — DropPartner URL keeps working"); refetchDomains(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const store = data?.store;
  const storeUrl = store ? `${window.location.origin}/store/${store.slug}` : null;
  const items = storeProducts?.items || [];
  const byCat = items.reduce((m, p) => { (m[p.category || "Uncategorized"] = m[p.category || "Uncategorized"] || []).push(p); return m; }, {});
  const plan = planData?.plan;
  const usedPct = plan ? Math.min(100, Math.round(((planData?.basket_used || 0) / (planData?.basket_limit || 1)) * 100)) : 0;

  return (
    <div data-testid="agent-store-page">
      <PageHeader title="My Store" sub="Your complete storefront — products, design, pages, branding and domain in one place." testid="store-title">
        {store && <StatusBadge status={store.status} />}
      </PageHeader>

      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap border border-slate-200 bg-white">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-store-products" disabled={!store}>{data ? `Products (${store?.product_count ?? 0})` : "Products"}</TabsTrigger>
          <TabsTrigger value="design" data-testid="tab-design" disabled={!store}>Design & Content</TabsTrigger>
          <TabsTrigger value="domain" data-testid="tab-domain" disabled={!store}>Domain</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-4" data-testid="store-overview">
          {justLive && store && (
            <div data-testid="store-live-banner" className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="text-emerald-500" size={28} />
                  <div>
                    <h3 className="text-lg font-bold text-emerald-700">YOUR STORE IS LIVE</h3>
                    <p className="font-mono text-xs text-emerald-600">{storeUrl}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!store ? (
            <div className="rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm" data-testid="store-setup-card">
              <Store size={34} className="mx-auto text-[#FF6B00]" />
              <h3 className="mt-4 text-lg font-bold text-[#0F2440]">Launch your storefront</h3>
              <p className="mx-auto mt-2 max-w-md text-xs leading-relaxed text-slate-500">
                One click creates your store with the Modern Luxury template and syncs your basket products.
                Then design it your way — 7 premium templates, AI images, pages, blog and your own domain.
              </p>
              <Button data-testid="setup-my-store-btn" onClick={() => setup.mutate()} disabled={setup.isPending}
                className="mt-6 bg-[#FF6B00] py-6 text-base font-bold text-white shadow-[0_0_30px_rgba(255,87,34,0.35)] hover:bg-[#E85D04]">
                {setup.isPending ? <Loader2 className="mr-2 animate-spin" size={16} /> : <Store size={16} className="mr-2" />}
                {setup.isPending ? "Setting up…" : "SET UP MY STORE"}
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="store-address-card">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-[#0F2440]"><Globe size={15} className="text-[#FF6B00]" /> Store address</h3>
                <p className="break-all rounded-lg bg-slate-50 px-3 py-2 font-mono text-xs text-slate-700">{storeUrl}</p>
                <div className="mt-3 space-y-2 text-xs">
                  <div className="flex justify-between"><span className="text-slate-500">Status</span><StatusBadge status={store.status} /></div>
                  <div className="flex justify-between"><span className="text-slate-500">Products in store</span><span className="font-semibold text-[#0F2440]" data-testid="overview-product-count">{store.product_count ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Custom domain</span><StatusBadge status={store.domain_status} /></div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <a href={storeUrl} target="_blank" rel="noreferrer">
                    <Button size="sm" data-testid="open-store-btn" className="bg-[#FF6B00] text-white hover:bg-[#E85D04]"><ExternalLink size={12} /> Open store</Button>
                  </a>
                  <Button size="sm" variant="outline" data-testid="copy-store-url-btn" className="border-slate-200 text-slate-600"
                    onClick={() => { navigator.clipboard.writeText(storeUrl); toast.success("Link copied"); }}>
                    <Copy size={12} /> Copy link
                  </Button>
                  <a data-testid="share-store-btn" href={`https://wa.me/?text=${encodeURIComponent(`Shop from my store: ${storeUrl}`)}`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" className="border-slate-200 text-slate-600"><Share2 size={12} /> Share</Button>
                  </a>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="plan-status-card">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-[#0F2440]"><Crown size={15} className="text-[#FF6B00]" /> Your plan</h3>
                {plan ? (
                  <>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-base font-bold text-[#0F2440]" data-testid="plan-name">{plan.name}</span>
                      <span className="text-slate-500">{fmtINR(plan.price)} one-time</span>
                    </div>
                    <div className="mt-3">
                      <div className="flex justify-between text-[11px] text-slate-500">
                        <span>Products</span>
                        <span data-testid="plan-usage">{planData.basket_used}/{planData.basket_limit}</span>
                      </div>
                      <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-100">
                        <div className={`h-full rounded-full ${usedPct >= 90 ? "bg-rose-500" : "bg-[#FF6B00]"}`} style={{ width: `${usedPct}%` }} />
                      </div>
                    </div>
                    <div className="mt-3 space-y-1.5 text-xs">
                      <div className="flex justify-between"><span className="text-slate-500">Activated</span><span className="text-slate-700">{fmtDate(planData.plan_activated_at)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Year-1 service free until</span><span className="text-slate-700">{fmtDate(planData.service_free_until)}</span></div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Renewal (₹{planData.annual_service_fee}/yr from year 2)</span>
                        <span data-testid="renewal-status" className={`font-semibold ${planData.renewal_status === "expired" ? "text-rose-500" : planData.renewal_status === "grace" ? "text-amber-500" : "text-emerald-600"}`}>
                          {planData.renewal_status === "expired" ? "Expired — renew now" : planData.renewal_status === "grace" ? `Overdue · grace till ${fmtDate(planData.service_renewal_due)}` : "Active"}
                        </span>
                      </div>
                    </div>
                    <Link to="/agent/plan">
                      <Button size="sm" variant="outline" data-testid="manage-plan-btn" className="mt-4 w-full border-slate-200 text-slate-600">Manage / upgrade plan</Button>
                    </Link>
                  </>
                ) : (
                  <div className="text-center">
                    <p className="text-xs text-slate-500">No active plan — pick one to unlock products and your store.</p>
                    <Link to="/agent/plan">
                      <Button size="sm" data-testid="choose-plan-btn" className="mt-4 bg-[#FF6B00] text-white hover:bg-[#E85D04]">Choose a plan</Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        {/* PRODUCTS */}
        <TabsContent value="products" className="mt-4" data-testid="store-products-tab">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-slate-500">
              Products auto-sync from your basket and appear on your live store with your selling price.
              {planData?.plan && <span className="ml-1 font-semibold text-[#0F2440]">Plan limit: {planData.basket_used}/{planData.basket_limit} products.</span>}
            </p>
            <Button size="sm" variant="outline" data-testid="sync-store-btn" onClick={() => syncStore.mutate()} disabled={syncStore.isPending}
              className="border-slate-200 text-slate-600">
              <RefreshCw size={11} className={syncStore.isPending ? "animate-spin" : ""} /> Sync now
            </Button>
          </div>
          {!items.length ? (
            <EmptyState title="No products in your store yet" sub="Add products to your basket from the Products page — they appear here and on your live store automatically." />
          ) : (
            Object.entries(byCat).map(([cat, prods]) => (
              <div key={cat} className="mb-6" data-testid={`cat-group-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
                <h3 className="mb-2 text-sm font-bold text-[#0F2440]">{cat} <span className="text-xs font-normal text-slate-400">({prods.length})</span></h3>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {prods.map((p) => (
                    <div key={p.id} data-testid={`store-product-${p.sku}`} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                      {p.images?.[0] && <img src={imgUrl(p.images[0])} alt="" className="mb-2 h-24 w-full rounded-lg object-cover" />}
                      <p className="truncate text-xs font-semibold text-[#0F2440]">{p.name}</p>
                      <p className="font-mono text-[10px] text-slate-400">{p.sku}</p>
                      <div className="mt-1.5 space-y-0.5 text-[11px]">
                        <div className="flex justify-between"><span className="text-slate-500">Your price</span><span className="font-bold text-[#E85D04]">{fmtINR(p.store_price)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Stock</span><span className={p.available > 0 ? "text-emerald-600" : "text-rose-500"}>{p.available} available</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Est. profit/unit</span><span className="font-semibold text-emerald-600">{fmtINR(Math.max(0, (p.store_price || 0) - (p.agent_cost || 0)))}</span></div>
                      </div>
                      <div className="mt-2 flex justify-end">
                        <button data-testid={`remove-store-product-${p.sku}`} onClick={() => removeProduct.mutate(p.id)}
                          className="text-slate-400 transition-colors hover:text-rose-500"><Trash2 size={12} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </TabsContent>

        {/* DESIGN — full store builder embedded */}
        <TabsContent value="design" className="mt-4" data-testid="store-design-tab">
          <StoreBuilder embedded />
        </TabsContent>

        {/* DOMAIN */}
        <TabsContent value="domain" className="mt-4" data-testid="store-domain-tab">
          <div className="max-w-2xl space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="domain-status-card">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-[#0F2440]"><Globe size={15} className="text-[#FF6B00]" /> Store address</h3>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between gap-2"><span className="shrink-0 text-slate-500">DropPartner URL (always works)</span><span className="break-all font-mono text-slate-700">{storeUrl}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Store</span><StatusBadge status={store?.status} /></div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="mb-1 text-sm font-bold text-[#0F2440]">Custom domains</h3>
              <p className="mb-4 text-[11px] leading-relaxed text-slate-500">
                Connect your own domain (e.g. shop.yourbrand.com or yourbrand.com). You only add DNS records at your own
                provider — your store keeps working at the DropPartner URL the whole time.
              </p>

              <div className="flex gap-2">
                <Input data-testid="domain-input" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="shop.yourbrand.com"
                  className="border-slate-200 bg-white font-mono text-sm text-[#0F2440]" />
                <Button data-testid="connect-domain-btn" onClick={() => connectDomain.mutate()} disabled={connectDomain.isPending || !domain.trim()}
                  className="bg-[#FF6B00] font-semibold text-white hover:bg-[#E85D04]">Connect domain</Button>
              </div>

              {connectInfo && (
                <div data-testid="domain-txt-instructions" className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-xs">
                  <p className="mb-2 font-semibold text-amber-700">Step 1 — verify you own {connectInfo.domain}</p>
                  <p className="mb-2 text-slate-500">Add this TXT record at your domain provider (DNS Management):</p>
                  <div className="rounded border border-slate-200 bg-white px-3 py-2 font-mono text-[11px] text-slate-700">
                    <div className="flex items-center justify-between gap-2">
                      <span>Type <b>TXT</b> · Host <b>{connectInfo.verification_txt.host}</b></span>
                      <button data-testid="copy-txt-value" className="text-[#FF6B00] hover:text-[#E85D04]"
                        onClick={() => { navigator.clipboard.writeText(connectInfo.verification_txt.value); toast.success("Copied"); }}>
                        <Copy size={12} />
                      </button>
                    </div>
                    <p className="mt-1 break-all">Value: <b>{connectInfo.verification_txt.value}</b></p>
                  </div>
                  <p className="mt-2 text-[10px] text-slate-400">{connectInfo.dns_note}</p>
                </div>
              )}

              {(domainsData?.domains || []).length > 0 && (
                <div className="mt-4 space-y-3" data-testid="domains-list">
                  {domainsData.domains.map((d) => (
                    <div key={d.id} data-testid={`domain-card-${d.domain}`} className="rounded-lg border border-slate-200 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <p className="font-mono text-sm font-bold text-[#0F2440]">{d.domain}
                            {d.is_primary && <span data-testid={`primary-badge-${d.domain}`} className="ml-2 rounded bg-[#FF6B00]/10 px-1.5 py-0.5 text-[9px] font-bold text-[#E85D04]">PRIMARY</span>}
                          </p>
                          <p className="text-[10px] text-slate-400">{d.domain_type === "SUBDOMAIN" ? "Subdomain" : "Root/custom domain"}</p>
                        </div>
                        <span data-testid={`domain-status-${d.domain}`} className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${
                          d.status === "ACTIVE" ? "bg-emerald-100 text-emerald-700"
                          : d.status === "VERIFIED" ? "bg-sky-100 text-sky-700"
                          : d.status === "SUSPENDED" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                          {d.status === "PENDING_VERIFICATION" ? "PENDING VERIFICATION" : d.status}
                        </span>
                      </div>

                      {d.status === "PENDING_VERIFICATION" && (
                        <Button data-testid={`verify-${d.domain}`} size="sm" onClick={() => verifyDomain.mutate(d.id)} disabled={verifyDomain.isPending}
                          className="mt-3 bg-amber-500 text-white hover:bg-amber-400">
                          {verifyDomain.isPending ? <Loader2 className="mr-1 animate-spin" size={12} /> : null} Verify domain
                        </Button>
                      )}

                      {d.status === "VERIFIED" && (
                        <div className="mt-3 space-y-2">
                          <p className="text-[11px] font-semibold text-sky-700">Step 2 — point your domain to DropPartner:</p>
                          {(connectRecords[d.id] || []).map((r, i) => (
                            <div key={i} className="rounded border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-[11px] text-slate-700">
                              Type <b>{r.type}</b> · Host <b>{r.host}</b> → <span className="text-[#E85D04]">{r.value}</span>
                              <p className="mt-0.5 font-sans text-[10px] text-slate-400">{r.note}</p>
                            </div>
                          ))}
                          <Button data-testid={`check-connection-${d.domain}`} size="sm" onClick={() => activateDomain.mutate(d.id)} disabled={activateDomain.isPending}
                            className="bg-[#FF6B00] text-white hover:bg-[#E85D04]">Check connection</Button>
                        </div>
                      )}

                      {d.status === "ACTIVE" && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <span className="rounded bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-700">Live · SSL active</span>
                          <a data-testid={`visit-${d.domain}`} href={`https://${d.domain}`} target="_blank" rel="noreferrer"
                            className="rounded border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-600 hover:bg-slate-50">
                            Visit <ExternalLink size={9} className="ml-0.5 inline" />
                          </a>
                          {!d.is_primary && (
                            <button data-testid={`set-primary-${d.domain}`} onClick={() => primaryDomain.mutate(d.id)}
                              className="rounded border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-600 hover:bg-slate-50">Set as primary</button>
                          )}
                        </div>
                      )}
                      {d.status === "SUSPENDED" && <p className="mt-2 text-[10px] text-rose-500">Suspended by DropPartner — contact support.</p>}
                      <button data-testid={`disconnect-${d.domain}`} onClick={() => { if (window.confirm(`Disconnect ${d.domain}? Your DropPartner store URL keeps working.`)) disconnectDomain.mutate(d.id); }}
                        className="mt-2 text-[10px] text-slate-400 underline hover:text-rose-500">Disconnect</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
